// The pick-mode overlay, ported from the résumé's bugPicker.ts with two structural changes:
// (1) the overlay lives in a Shadow DOM with its own styles, so it cannot collide with any target
// app's CSS (the original leaned on the résumé's stylesheet); (2) picks are cancellable from
// outside (the shell's pick-cancel message) via the returned handle. The event-suppression
// choreography — mouse-family frozen, touch scrolls allowed, one-shot trailing-click swallow —
// is preserved verbatim in behavior; see the original for the full rationale.
import type { ElementContext } from "@redliner/protocol";
import type { ResolvedConfig } from "./config.js";
import { buildElementContext, describe } from "./context.js";

const SHADOW_CSS = `
:host { all: initial; }
.outline {
  position: fixed; z-index: 2147483000; display: none; pointer-events: none;
  border: 1.5px solid var(--rl-accent); border-radius: 4px;
  background: color-mix(in srgb, var(--rl-accent) 8%, transparent);
  box-shadow: 0 0 0 4px color-mix(in srgb, var(--rl-accent) 12%, transparent);
}
.label {
  position: fixed; z-index: 2147483001; display: none; pointer-events: none;
  max-width: 60vw; overflow: hidden; white-space: nowrap; text-overflow: ellipsis;
  font: 11px/1.4 ui-monospace, monospace; letter-spacing: 0.02em; padding: 4px 9px;
  border-radius: 6px; color: var(--rl-ink); background: var(--rl-card);
  border: 1px solid color-mix(in srgb, var(--rl-ink) 14%, transparent);
  box-shadow: 0 2px 10px color-mix(in srgb, var(--rl-ink) 12%, transparent);
}
.bar {
  position: fixed; z-index: 2147483001; left: 50%; bottom: 18px; transform: translateX(-50%);
  display: flex; align-items: center; gap: 12px;
  font: 12px/1.4 ui-monospace, monospace; padding: 8px 14px; border-radius: 999px;
  color: var(--rl-ink); background: var(--rl-card);
  border: 1px solid color-mix(in srgb, var(--rl-ink) 14%, transparent);
  box-shadow: 0 4px 18px color-mix(in srgb, var(--rl-ink) 16%, transparent);
}
.cancel { font: inherit; color: var(--rl-accent); background: none; border: none; padding: 0; cursor: pointer; }
`;

// Crosshair cursor must apply to the PAGE, which shadow styles cannot reach — one namespaced
// document-level style, added and removed with the session.
const CURSOR_CSS = `.redliner-picking, .redliner-picking * { cursor: crosshair !important; }`;

export interface PickHandle {
  result: Promise<ElementContext | null>;
  cancel(): void;
}

let busyUntil = 0;
/** True while a pick session is active (plus the trailing-synthetic-click grace window). */
export function pickerBusy(): boolean {
  return Date.now() < busyUntil;
}

export function startPick(cfg: ResolvedConfig): PickHandle {
  busyUntil = Number.MAX_SAFE_INTEGER;
  let cancelFn: () => void = () => {};
  const result = new Promise<ElementContext | null>((resolve) => {
    const host = document.createElement("div");
    host.style.setProperty("--rl-accent", cfg.theme.accent);
    host.style.setProperty("--rl-ink", cfg.theme.ink);
    host.style.setProperty("--rl-card", cfg.theme.card);
    const shadow = host.attachShadow({ mode: "closed" });
    const style = document.createElement("style");
    style.textContent = SHADOW_CSS;
    const outline = document.createElement("div");
    outline.className = "outline";
    const label = document.createElement("div");
    label.className = "label";
    const bar = document.createElement("div");
    bar.className = "bar";
    const barText = document.createElement("span");
    barText.textContent = "Click or tap the element in question";
    const cancel = document.createElement("button");
    cancel.type = "button";
    cancel.className = "cancel";
    cancel.textContent = "Cancel (Esc)";
    bar.append(barText, cancel);
    shadow.append(style, outline, label, bar);
    const cursorStyle = document.createElement("style");
    cursorStyle.textContent = CURSOR_CSS;
    document.head.append(cursorStyle);
    document.body.append(host);
    document.documentElement.classList.add("redliner-picking");

    // "Ours" = our shadow overlay (document-level events retarget to the host) or host-app chrome
    // the config exempts (e.g. the shell-side tray rendered inside the target's own DOM).
    const ours = (t: EventTarget | null) =>
      t === host ||
      (t instanceof Element &&
        (t.closest(".redliner-picking-exempt") !== null ||
          cfg.ignoreSelectors.some((sel) => t.closest(sel) !== null)));

    let downPos: { x: number; y: number } | null = null;
    let settled = false;

    // Prefer the event's real target; fall back to hit-testing. Keyboard-synthesized clicks can
    // carry (0,0) coordinates — an indeterminate element is treated as "suppress and ignore".
    const targetOf = (e: MouseEvent): Element | null => {
      if (e.target instanceof Element && e.target !== host && !ours(e.target)) return e.target;
      const el = document.elementFromPoint(e.clientX, e.clientY);
      return el && !ours(el) ? el : null;
    };

    const onMove = (e: MouseEvent) => {
      const el = document.elementFromPoint(e.clientX, e.clientY);
      if (!el || ours(el)) {
        outline.style.display = "none";
        label.style.display = "none";
        return;
      }
      const r = el.getBoundingClientRect();
      outline.style.cssText = `display:block;left:${r.left}px;top:${r.top}px;width:${r.width}px;height:${r.height}px`;
      label.textContent = describe(el, cfg);
      label.style.display = "block";
      const above = r.top > 34;
      label.style.left = `${Math.max(8, r.left)}px`;
      label.style.top = above ? `${r.top - 30}px` : `${r.bottom + 6}px`;
    };

    const suppress = (e: Event) => {
      if (ours(e.target)) return;
      e.preventDefault();
      e.stopPropagation();
    };
    const onPointerDown = (e: PointerEvent) => {
      if (ours(e.target)) return;
      downPos = { x: e.clientX, y: e.clientY };
      if (e.pointerType === "touch") {
        e.stopPropagation(); // app handlers no; native scroll yes
      } else {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    const onPointerUp = (e: PointerEvent) => {
      if (ours(e.target)) return;
      e.preventDefault();
      e.stopPropagation();
      if (e.pointerType === "touch" && downPos) {
        const moved = Math.hypot(e.clientX - downPos.x, e.clientY - downPos.y);
        if (moved > 10) return; // that was a scroll, not a tap
      }
      const el = targetOf(e);
      if (el) finish(buildElementContext(el, cfg));
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        finish(null);
      }
    };
    const clickSuppressor = (e: MouseEvent) => {
      if (ours(e.target)) return;
      e.preventDefault();
      e.stopPropagation();
      if (settled) document.removeEventListener("click", clickSuppressor, true);
    };

    const cleanup = () => {
      document.removeEventListener("mousemove", onMove, true);
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("pointerup", onPointerUp, true);
      document.removeEventListener("mousedown", suppress, true);
      document.removeEventListener("mouseup", suppress, true);
      document.removeEventListener("keydown", onKey, true);
      busyUntil = Date.now() + 450;
      window.setTimeout(() => document.removeEventListener("click", clickSuppressor, true), 400);
      host.remove();
      cursorStyle.remove();
      document.documentElement.classList.remove("redliner-picking");
    };
    const finish = (ctx: ElementContext | null) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(ctx);
    };
    cancelFn = () => finish(null);

    cancel.addEventListener("click", (e) => {
      e.stopPropagation();
      finish(null);
    });
    document.addEventListener("mousemove", onMove, true);
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("pointerup", onPointerUp, true);
    document.addEventListener("mousedown", suppress, true);
    document.addEventListener("mouseup", suppress, true);
    document.addEventListener("click", clickSuppressor, true);
    document.addEventListener("keydown", onKey, true);
  });
  return { result, cancel: () => cancelFn() };
}
